const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isChatMessage (message) {
  if (!message || typeof message !== 'object' || Array.isArray(message)) return false
  const hasId = value => /^(?:[1-9]\d*)$/.test(String(value))
  const hasDate = value => typeof value === 'string' && !Number.isNaN(Date.parse(value))
  return hasId(message.id) &&
    hasId(message.author_id) && hasId(message.user_id) &&
    typeof message.message === 'string' && message.message.trim().length > 0 &&
    hasDate(message.created_at) && (message.read_at === null || hasDate(message.read_at)) &&
    (message.client_message_id == null || (typeof message.client_message_id === 'string' && UUID.test(message.client_message_id)))
}

export function createClientMessageId () {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function chatMessageKey (message) {
  return message.client_message_id
    ? `client:${message.author_id}:${message.client_message_id}`
    : `server:${message.id}`
}

function sameMessage (a, b) {
  if (a.id != null && b.id != null && a.id == b.id) return true
  return !!a.client_message_id && a.client_message_id === b.client_message_id &&
    a.author_id == b.author_id && a.user_id == b.user_id
}

// Arrays are newest-first (the view uses column-reverse). Only new entries
// are inserted; updates never move an already displayed message.
export function mergeChatMessages (current, incoming, placement = 'newer') {
  const existing = current.slice()
  const added = []
  for (const message of incoming) {
    const list = existing.some(item => sameMessage(item, message)) ? existing : added
    const index = list.findIndex(item => sameMessage(item, message))
    if (index === -1) {
      added.push(message)
    } else {
      const previous = list[index]
      if (previous.id != null && message.id == null) continue
      list[index] = {
        ...previous,
        ...message,
        read_at: message.read_at || (previous.id != null ? previous.read_at : message.read_at)
      }
      if (list[index].id != null) {
        delete list[index].delivery_state
        delete list[index].retryable
        delete list[index].delivery_order
      }
    }
  }
  return placement === 'older' ? existing.concat(added) : added.concat(existing)
}

// Only a manually retried message newly created by the server may move.
// Keep later queued sends/newer saved messages below it (arrays are reversed).
export function placeRetriedChatMessage (current, message, order) {
  const merged = mergeChatMessages(current, [message])
  const item = merged.find(m => sameMessage(m, message))
  const others = merged.filter(m => !sameMessage(m, message))
  let position = 0
  others.forEach((m, index) => {
    if ((m.id != null && Number(m.id) > Number(message.id)) || (m.id == null && m.delivery_order > order)) position = index + 1
  })
  others.splice(position, 0, item)
  return others
}

export function oldestChatMessageId (messages) {
  const ids = messages.filter(message => message.id != null).map(message => Number(message.id))
  return ids.length ? Math.min(...ids) : null
}
